import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sheru } from "@/components/Sheru";

export const PostQuizSheru = ({ score, wrongAnswers = [], topic, nextTopicSuggestion, whatsappMessage, onNextTopic }) => {
  const [showSecondMessage, setShowSecondMessage] = useState(false);
  const [showNextTopic, setShowNextTopic] = useState(false);

  // For perfect score: switch to second message after 2s
  useEffect(() => {
    if (score === 4) {
      const t = setTimeout(() => setShowSecondMessage(true), 2000);
      return () => clearTimeout(t);
    }
  }, [score]);

  // Show next-topic section after 2s delay
  useEffect(() => {
    if (nextTopicSuggestion) {
      const t = setTimeout(() => setShowNextTopic(true), 2000);
      return () => clearTimeout(t);
    }
  }, [nextTopicSuggestion]);

  const topicShort = topic ? topic.slice(0, 40) + (topic.length > 40 ? "..." : "") : "this topic";

  const getMessage = () => {
    if (score === 4) {
      return showSecondMessage
        ? `Adding "${topicShort}" to your Trophy Cabinet! 🎖️`
        : `You've completely mastered ${topicShort}! 🏆`;
    }
    if (score === 3) return `Excellent work! 🌟 You got one question slightly wrong. Here's a quick tip:`;
    return `Great try! 💪 Let me explain the tricky parts:`;
  };

  const sheruState = score >= 3 ? "celebrating" : "idle";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="space-y-3"
    >
      {/* Sheru with message */}
      <div className="flex items-start gap-3 p-4 rounded-2xl bg-card border border-border">
        <div className="flex-shrink-0">
          <Sheru state={sheruState} message={null} size="sm" />
        </div>
        <div className="flex-1 pt-1">
          <AnimatePresence mode="wait">
            <motion.p
              key={getMessage()}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="text-sm font-heading font-semibold text-foreground leading-snug"
            >
              {getMessage()}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>

      {/* Tip cards for wrong answers */}
      {wrongAnswers.length > 0 && score < 4 && (
        <div className="space-y-2">
          {wrongAnswers.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 + 0.2 }}
              className="rounded-xl border border-[#448AFF]/30 bg-[#448AFF]/5 p-3 text-left space-y-1"
            >
              <p className="text-xs font-heading font-semibold text-[#448AFF]">
                💡 Correct answer: {item.correctOption}
              </p>
              <p className="text-xs text-foreground/80 leading-relaxed">
                {item.explanation}
              </p>
            </motion.div>
          ))}
        </div>
      )}

      {/* Next topic suggestion — appears after 2s */}
      <AnimatePresence>
        {showNextTopic && nextTopicSuggestion && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-2"
          >
            {nextTopicSuggestion.should_revisit ? (
              /* Revisit path: score <= 50% */
              <div className="rounded-2xl border border-[#448AFF]/30 bg-[#448AFF]/5 p-4 space-y-3">
                <p className="text-sm font-heading font-semibold text-foreground">
                  Almost there, champion! 💪 Let's master{" "}
                  <span className="text-[#448AFF]">{topicShort}</span> one more time before we move forward!
                </p>
                <button
                  onClick={() => onNextTopic?.(topic)}
                  className="w-full rounded-xl bg-[#448AFF] text-white text-sm font-heading font-bold py-2.5 hover:bg-[#D45A20] transition-colors"
                >
                  Try {topicShort} again! 🔄
                </button>
              </div>
            ) : (
              /* Next topic path: score > 50% */
              <div className="rounded-2xl border border-[#FFD740]/40 bg-[#FFD740]/5 p-4 space-y-3">
                <p className="text-sm font-heading font-semibold text-foreground">
                  You're ready for the next adventure! 🐯
                </p>
                <div className="space-y-0.5">
                  <p className="text-base font-heading font-bold text-foreground">
                    {nextTopicSuggestion.emoji} {nextTopicSuggestion.next_topic}
                  </p>
                  <p className="text-xs text-foreground/60">{nextTopicSuggestion.reason}</p>
                </div>
                <button
                  onClick={() => onNextTopic?.(nextTopicSuggestion.next_topic)}
                  className="w-full rounded-xl bg-gradient-to-r from-[#448AFF] to-[#FFD740] text-white text-sm font-heading font-bold py-2.5 hover:opacity-90 transition-opacity"
                >
                  Learn {nextTopicSuggestion.next_topic} now! {nextTopicSuggestion.emoji} →
                </button>
              </div>
            )}

            {/* WhatsApp parent notification — only when score >= 3 */}
            {whatsappMessage && (
              <button
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`)}
                className="w-full flex items-center justify-center gap-2 rounded-xl border border-[#25D366]/40 bg-[#25D366]/5 text-[#25D366] text-xs font-heading font-semibold py-2 hover:bg-[#25D366]/10 transition-colors"
              >
                <span>📱</span> Share with Parent
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
